"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Header from "@/components/Header";
import Stepper from "@/components/Stepper";
import FormField, { Input, Select, Textarea } from "@/components/FormField";
import { useAuth } from "@/contexts/AuthContext";
import { generateProtocolo } from "@/lib/store";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  FileText,
  Info,
  Search,
  Upload,
} from "lucide-react";

const STEPS = [
  { number: 1, title: "Alvará" },
  { number: 2, title: "Formulário" },
  { number: 3, title: "Responsáveis" },
  { number: 4, title: "Documentação" },
  { number: 5, title: "Rascunho" },
  { number: 6, title: "Emissão" },
];

export default function RenovacaoPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [emitido, setEmitido] = useState(false);
  const [protocolo, setProtocolo] = useState("");
  const [alvaraEncontrado, setAlvaraEncontrado] = useState(false);

  const [form, setForm] = useState({
    ren_numAlvara: "",
    ren_alterouCaracteristicas: "nao",
    ren_alterouProjeto: "nao",
    ren_alterouArea: "nao",
    ren_observacoes: "",
    ren_tipoPessoa: "fisica",
    ren_cpfCnpj: "",
    ren_nome: "",
    ren_email: "",
    ren_telefone: "",
    ren_doc1: "",
    ren_doc2: "",
    ren_doc3: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePesquisar = () => {
    if (form.ren_numAlvara) setAlvaraEncontrado(true);
  };

  const nextStep = () => {
    if (step < 6) setStep(step + 1);
    window.scrollTo(0, 0);
  };
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleEmitir = () => {
    setProtocolo(generateProtocolo());
    setEmitido(true);
  };

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Acesso Necessário</h2>
          <p className="text-gray-500 mb-6">Para renovar um alvará, você precisa estar logado.</p>
          <button onClick={() => router.push("/login")} className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition">
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  if (emitido) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-success-light rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-success" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Alvará Renovado!</h2>
          <p className="text-gray-500 mb-2">Novo protocolo:</p>
          <p className="text-2xl font-mono font-bold text-primary mb-8">{protocolo}</p>
          <div className="flex justify-center gap-4">
            <button className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold">
              <Download size={18} /> Baixar Alvará
            </button>
            <button onClick={() => router.push("/dashboard")} className="flex items-center gap-2 border border-gray-300 px-6 py-3 rounded-xl font-semibold text-gray-600">
              Ver Meus Alvarás
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Renovação de Alvará de Construção</h1>
          <p className="text-sm text-gray-500 mt-1">Informe o alvará que deseja renovar</p>
        </div>

        <Stepper steps={STEPS} currentStep={step} />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 lg:p-8">
          {/* STEP 1 - Alvará */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Informar Alvará</h2>
                <p className="text-sm text-gray-500 mt-1">Digite o número do alvará que deseja renovar</p>
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <FormField label="Número do Alvará" required>
                    <Input name="ren_numAlvara" value={form.ren_numAlvara} onChange={handleChange} placeholder="ALV-2024-000123" />
                  </FormField>
                </div>
                <div className="flex items-end">
                  <button onClick={handlePesquisar} className="bg-primary text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 hover:bg-primary-dark transition">
                    <Search size={18} /> Pesquisar
                  </button>
                </div>
              </div>

              {alvaraEncontrado && (
                <div className="border border-green-200 bg-green-50 rounded-xl p-5 mt-4">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <Check size={18} /> Alvará Encontrado
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Protocolo:</span> <strong>{form.ren_numAlvara}</strong></div>
                    <div><span className="text-gray-500">Status:</span> <strong className="text-green-700">Emitido</strong></div>
                    <div><span className="text-gray-500">Endereço:</span> <strong>Rua Principal, 100 - Centro</strong></div>
                    <div><span className="text-gray-500">Data de Emissão:</span> <strong>15/01/2024</strong></div>
                    <div><span className="text-gray-500">Proprietário:</span> <strong>João Silva</strong></div>
                    <div><span className="text-gray-500">Validade:</span> <strong className="text-amber-600">15/01/2029</strong></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 - Formulário */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Formulário de Renovação</h2>
                <p className="text-sm text-gray-500 mt-1">Responda as perguntas sobre o estado atual do empreendimento</p>
              </div>

              <FormField label="Houve alteração nas características do projeto?" required>
                <Select name="ren_alterouCaracteristicas" value={form.ren_alterouCaracteristicas} onChange={handleChange}>
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </Select>
              </FormField>

              {form.ren_alterouCaracteristicas === "sim" && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Atenção:</strong> Caso tenha havido alteração nas características do projeto, é necessária a emissão de um novo Alvará de Construção.
                  </p>
                </div>
              )}

              {form.ren_alterouCaracteristicas === "nao" && (
                <>
                  <FormField label="Houve alteração no projeto arquitetônico?">
                    <Select name="ren_alterouProjeto" value={form.ren_alterouProjeto} onChange={handleChange}>
                      <option value="nao">Não</option>
                      <option value="sim">Sim</option>
                    </Select>
                  </FormField>
                  <FormField label="Houve alteração na área construída?">
                    <Select name="ren_alterouArea" value={form.ren_alterouArea} onChange={handleChange}>
                      <option value="nao">Não</option>
                      <option value="sim">Sim</option>
                    </Select>
                  </FormField>
                  <FormField label="Observações">
                    <Textarea name="ren_observacoes" value={form.ren_observacoes} onChange={handleChange} placeholder="Observações adicionais..." />
                  </FormField>
                </>
              )}
            </div>
          )}

          {/* STEP 3 - Responsáveis */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Dados do Requerente</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Tipo de Pessoa" required>
                  <Select name="ren_tipoPessoa" value={form.ren_tipoPessoa} onChange={handleChange}>
                    <option value="fisica">Pessoa Física</option>
                    <option value="juridica">Pessoa Jurídica</option>
                  </Select>
                </FormField>
                <FormField label={form.ren_tipoPessoa === "fisica" ? "CPF" : "CNPJ"} required>
                  <Input name="ren_cpfCnpj" value={form.ren_cpfCnpj} onChange={handleChange} />
                </FormField>
              </div>
              <FormField label="Nome Completo / Razão Social" required>
                <Input name="ren_nome" value={form.ren_nome} onChange={handleChange} />
              </FormField>
              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="E-mail" required>
                  <Input name="ren_email" type="email" value={form.ren_email} onChange={handleChange} />
                </FormField>
                <FormField label="Telefone" required>
                  <Input name="ren_telefone" value={form.ren_telefone} onChange={handleChange} />
                </FormField>
              </div>
            </div>
          )}

          {/* STEP 4 - Documentação */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Documentação</h2>
                <p className="text-sm text-gray-500 mt-1">Anexe os documentos para renovação</p>
              </div>
              {[
                { key: "ren_doc1", label: "ART/RRT do Projeto Atualizada", required: true },
                { key: "ren_doc2", label: "ART/RRT da Execução Atualizada", required: true },
                { key: "ren_doc3", label: "Documento Pessoal", required: true },
              ].map((doc) => (
                <div key={doc.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-primary/50 transition">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{doc.label} {doc.required && <span className="text-danger">*</span>}</p>
                      <p className="text-xs text-gray-400">{(form as Record<string, string>)[doc.key] || "Nenhum arquivo selecionado"}</p>
                    </div>
                  </div>
                  <label className="cursor-pointer bg-primary-light text-primary px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary hover:text-white transition flex items-center gap-2">
                    <Upload size={16} /> Enviar
                    <input type="file" accept=".pdf" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setForm({ ...form, [doc.key]: file.name });
                    }} />
                  </label>
                </div>
              ))}
            </div>
          )}

          {/* STEP 5 - Rascunho */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Revisão</h2>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800"><strong>Atenção:</strong> Verifique todos os dados antes de prosseguir. Após confirmação, não será possível modificar.</p>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3"><h3 className="font-semibold text-gray-800 text-sm">Dados da Renovação</h3></div>
                <div className="p-4 grid md:grid-cols-2 gap-3">
                  <div><p className="text-xs text-gray-400">Alvará Original</p><p className="text-sm font-medium">{form.ren_numAlvara || "-"}</p></div>
                  <div><p className="text-xs text-gray-400">Requerente</p><p className="text-sm font-medium">{form.ren_nome || "-"}</p></div>
                  <div><p className="text-xs text-gray-400">CPF/CNPJ</p><p className="text-sm font-medium">{form.ren_cpfCnpj || "-"}</p></div>
                  <div><p className="text-xs text-gray-400">Alterou Características</p><p className="text-sm font-medium">{form.ren_alterouCaracteristicas === "sim" ? "Sim" : "Não"}</p></div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 6 - Emissão */}
          {step === 6 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Emissão da Renovação</h2>
              </div>
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-800 mb-3">Confirmações</h3>
                <label className="flex items-start gap-3 cursor-pointer mb-3">
                  <input type="checkbox" className="mt-1 w-4 h-4 accent-primary" />
                  <span className="text-sm text-gray-600">Declaro que as informações são verdadeiras e me responsabilizo pela veracidade dos dados.</span>
                </label>
              </div>
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-800 mb-3">Pagamento da Taxa</h3>
                <div className="bg-amber-50 p-4 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Taxa de Renovação</p>
                    <p className="text-xs text-gray-400">Valor conforme legislação</p>
                  </div>
                  <span className="text-lg font-bold text-amber-700">R$ 100,00</span>
                </div>
              </div>
              <button onClick={handleEmitir} className="w-full bg-primary text-white py-4 rounded-xl text-lg font-bold hover:bg-primary-dark transition shadow-lg">
                RENOVAR ALVARÁ
              </button>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button onClick={prevStep} disabled={step === 1} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition ${step === 1 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-gray-100"}`}>
              <ArrowLeft size={18} /> Anterior
            </button>
            {step < 6 && (
              <button onClick={nextStep} className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition">
                Próximo <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
